/**
 * The account's registry — which applications exist and which projects are in them — as HQ
 * holds it (ADR 0002). HQ is its only writer; the client reads it through HQ's door
 * (`GET /api/structure`, filtered to what the reader sees in Zerops) and writes it through
 * `POST /api/apps` and `POST /api/apps/{id}/projects`.
 *
 * An application is a group: its HQ id is the group's id.
 *
 * Pure: no network (rule R1).
 *
 * @module hq/registry
 */
import type { RoleProjectKind } from "@t3tools/shared/zeropsRoles";

import { canonicalEnvironmentsOf } from "./environments.ts";
import type { HqStructure, CanonicalHqStructure } from "./client.ts";
import { isRoleProjectKind } from "./placement.ts";

export interface ZeropsRegistryProject {
  readonly projectId: string;
  readonly kind: RoleProjectKind;
}

export interface ZeropsRegistryGroup {
  readonly groupId: string;
  readonly name: string;
  readonly projects: ReadonlyArray<ZeropsRegistryProject>;
}

export interface ZeropsRegistry {
  readonly groups: ReadonlyArray<ZeropsRegistryGroup>;
}

export const EMPTY_REGISTRY: ZeropsRegistry = { groups: [] };

export function registryFromHq(structure: HqStructure | CanonicalHqStructure): ZeropsRegistry {
  return {
    groups: structure.apps.map((app) => {
      const environments = canonicalEnvironmentsOf(app.environments) ?? [];
      const targets = new Set<string>(
        environments.flatMap((environment) =>
          environment.target.kind === "zerops" ? [environment.target.projectId] : [],
        ),
      );
      return {
        groupId: app.id,
        name: app.name,
        projects: [
          ...app.projects.flatMap(({ projectId, kind }) =>
            isRoleProjectKind(kind) &&
            (kind === "mate" || kind === "devstage" || !targets.has(projectId))
              ? [{ projectId, kind }]
              : [],
          ),
          ...environments.flatMap((environment) =>
            environment.target.kind === "zerops"
              ? [{ projectId: environment.target.projectId, kind: environment.tier }]
              : [],
          ),
        ],
      };
    }),
  };
}
